/* eslint-disable max-len */
import {
  Create,
  SimpleForm,
  TextInput,
  useCreate,
  useNotify,
  useTranslate
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import CustomHeaderActions from "./CustomHeaderActions";
import PhoneInput from "./PhoneInput";
import CustomImageInputSmall from "./CustomImageInputSmall";
import { DefaultAvatarIcon } from "assets/Icons";
import { usePhotoUploadSubmitCreate } from "hooks/usePhotoUploadSubmitCreate";
import CustomPronounSelect from "./CustomPronounSelect";
import AdvancedDetails from "./AdvancedDetails";
import { useState } from "react";
import { correctDate } from "../utils/correctDate";
import { CustomToolbar } from "./CustomToolbar";
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { stripePromise, CARD_ELEMENT_OPTIONS } from "stripeConfig";
import CustomDayAndMonthPicker from "./CustomDayAndMonthPicker";
import { isPhoneValid } from "../utils/isPhoneValid";

export default function UserCreateContainer() {
  return (
    <Elements stripe={stripePromise}>
      <UserCreate />
    </Elements>
  );
}

function UserCreate() {
  const { permissions } = User.useUser();
  const { api } = Axios.useAxios();
  const translate = useTranslate();
  const [create] = useCreate();
  const navigate = useNavigate();
  const notify = useNotify();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewCardValid, setIsNewCardValid] = useState<boolean>(false);

  const { submit: submitWithPhoto } = usePhotoUploadSubmitCreate({
    redirect: `/dashboard/User`,
    resource: "User",
    keys: ["photo_url"],
    setIsLoading: setIsLoading
  });

  const submit = (data: any) => {
    setIsLoading(true);
    if (data.fakeDirtyFormWorkaround) {
      delete data.fakeDirtyFormWorkaround;
    }
    const payload = {
      ...data,
      joined_company: { id: permissions.companyId }
    };

    if (data.email) {
      payload.email = data.email.toLowerCase();
    }

    if (data.birthday) {
      payload.birthday = correctDate(data.birthday);
    }

    const customOnSuccess = async (customerUser: any) => {
      create("CompanyUserDescription", {
        data: {
          description: data.description,
          user: { id: customerUser.id },
          company: { id: permissions?.companyId }
        }
      });
      await api.post(
        `${process.env.REACT_APP_API_CORE_URL}/v1/users/send-mail`,
        {
          id: customerUser.id,
          company_id: permissions.companyId,
          createArtist: false,
          createUser: true,
          isInvited: true
        }
      );

      if (stripe && elements && isNewCardValid) {
        const cardElement = elements.getElement(CardElement);

        if (cardElement) {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: `${data?.first_name} ${data?.last_name}`,
              email: data?.email?.toLowerCase(),
              phone: data?.phone_number
            }
          });

          if (error) {
            // no need to handle this error because at this point user is already created because we needed user id
            // if we will not redirect and show card errors user can not be created again
          } else {
            try {
              await api.post("/v1/payment-methods", {
                paymentMethod: {
                  id: paymentMethod?.id,
                  brand: paymentMethod?.card?.brand,
                  exp_month: paymentMethod?.card?.exp_month,
                  exp_year: paymentMethod?.card?.exp_year,
                  last4: paymentMethod?.card?.last4
                },
                user_id: customerUser.id
              });
            } catch (e) {
              // no need to handle this error because at this point user is already created because we needed user id
              // if we will not redirect and show card errors user can not be created again
            }
          }
        }
      }
      setIsLoading(false);
      navigate(
        window.location.pathname.includes("/dashboard/Customer")
          ? "/dashboard/Customer"
          : "/dashboard/User"
      );
    };

    const customOnError = (err: any) => {
      if (
        JSON.stringify(err).includes(
          "Unique constraint failed on the fields: (`email`)"
        )
      ) {
        notify(translate("users.this_email_has_already_been_registered"), {
          type: "error",
          undoable: false,
          anchorOrigin: { vertical: "bottom", horizontal: "center" }
        });
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    submitWithPhoto(payload, customOnSuccess, customOnError);
  };

  return (
    <Create
      actions={<CustomHeaderActions title={translate("users.customer")} />}
      sx={{
        "& .RaToolbar-desktopToolbar": {
          paddingLeft: "16px"
        }
      }}
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(validationSchema)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <CustomImageInputSmall
          source="photo_url"
          borderRadius="20px"
          buttonText={translate("common.set_photo")}
          bg="#B0ACA6"
          placeholder={<DefaultAvatarIcon />}
        />
        <TextInput
          source="first_name"
          label={"* " + translate("users.first_name")}
          fullWidth
        />
        <TextInput
          source="last_name"
          label={"* " + translate("users.last_name")}
          fullWidth
        />
        <TextInput source="email" label="Email" fullWidth error />
        <PhoneInput source="phone_number" fullWidth checkSubmitted />
        <CustomDayAndMonthPicker label={translate("users.birthday_optional")} />
        <CustomPronounSelect
          source="pronoun"
          label={translate("users.pronoun")}
          className="w-full"
        />
        <AdvancedDetails>
          <div>
            <TextInput
              source="description"
              label={translate("users.description")}
              fullWidth
            />
            <CardElement
              onChange={(e) => {
                if (e?.complete && !e?.error) {
                  setIsNewCardValid(true);
                } else {
                  setIsNewCardValid(false);
                }
              }}
              options={CARD_ELEMENT_OPTIONS}
            />
          </div>
        </AdvancedDetails>
      </SimpleForm>
    </Create>
  );
}

const validationSchema = yup
  .object()
  .shape({
    first_name: yup.string().required("First name required."),
    last_name: yup.string().required("Last name required."),
    email: yup
      .string()
      .email("Incorrectly formatted email.")
      .when("phone_number", {
        is: (phone_number: string) => !phone_number,
        then: yup
          .string()
          .required("Email required if phone number is not provided."),
        otherwise: yup.string().nullable()
      }),
    photo_url: yup.object().nullable(),
    phone_number: yup
      .string()
      .test("phone", "Invalid phone number", (value) => {
        return isPhoneValid(value);
      }),
    birthday: yup.date().nullable(),
    pronoun: yup.string()
  })
  .required();
